import { useEffect, useState } from "react";
import { Link } from "react-router";
import { AlertTriangle, Check } from "lucide-react";
import { getRuntimeConfig } from "@shared-core/config/runtime-config";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CatalogueMigrationOwnerListOptions } from "@shared-core/data/adapter";
import type {
  CatalogueMigrationOwnerRow,
  CatalogueMigrationStep,
  CatalogueMigrationStepCounts,
} from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import {
  MIGRATION_STEP_FILTERS,
  MIGRATION_STEP_LABELS,
  readMigrationStep,
} from "@shared-core/domain/catalogue-migration";
import { formatCount } from "@shared-core/domain/formatters";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { Badge } from "../app/components/primitives/badge";
import { Label } from "../app/components/primitives/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { CatalogueMigrationEnvironmentBadge } from "./catalogue-migration/environment";
import { selectClassName } from "./stock/StockFormField";

// Server-side filter, search, sort and paging (docs/list-endpoints.md): every
// change refetches and returns to page 1; rows render as returned.

const PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { value: "customer_name", label: "Customer" },
  { value: "step", label: "Step (in review first)" },
  { value: "-repeat_work_count", label: "Most repeat work" },
  { value: "-held_stock_count", label: "Most held stock" },
];

/**
 * Settings › Catalogue migration (admin only, temporary; was Stock cutover):
 * every customer with held stock or repeat work and its step on the way from
 * Tharstern to CIS. Read only: decisions are made in the customer's migration
 * file (the migrate-customer-catalogue skill), then applied at the next
 * deploy.
 */
export function SettingsCatalogueMigrationPage({
  environment = getRuntimeConfig().environment,
}: {
  /** The deployment environment; read from runtime config, overridable for stories. */
  environment?: string;
}) {
  const dataAdapter = useDataAdapter();
  const [rows, setRows] = useState<CatalogueMigrationOwnerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [counts, setCounts] = useState<CatalogueMigrationStepCounts | null>(
    null,
  );
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [step, setStep] = useState<CatalogueMigrationStep | "">("");
  const [sort, setSort] = useState(SORT_OPTIONS[0]!.value);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, step, sort]);

  useEffect(() => {
    let cancelled = false;
    const options: CatalogueMigrationOwnerListOptions = {
      page,
      pageSize: PAGE_SIZE,
      sort,
    };
    if (debouncedSearch.trim()) options.q = debouncedSearch.trim();
    if (step) options.step = step;
    setLoadError(false);
    dataAdapter
      .listCatalogueMigrationOwners(options)
      .then((result) => {
        if (cancelled) return;
        setRows(result.items);
        setTotal(result.total);
        setTotalPages(result.totalPages);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("catalogue_migration.owners.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
        setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setFirstLoad(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, page, debouncedSearch, step, sort]);

  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .getCatalogueMigrationStepCounts(debouncedSearch.trim() || undefined)
      .then((result) => !cancelled && setCounts(result))
      .catch((error: unknown) => {
        console.error("catalogue_migration.step_counts.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, debouncedSearch]);

  const isFiltered = Boolean(debouncedSearch.trim()) || step !== "";
  const chips: {
    value: CatalogueMigrationStep | "";
    label: string;
    count: number | null;
  }[] = [
    { value: "", label: "All", count: counts?.all ?? null },
    ...MIGRATION_STEP_FILTERS.filter(
      (s) => s !== "done" || (counts?.done ?? 0) > 0,
    ).map((s) => ({
      value: s,
      label: MIGRATION_STEP_LABELS[s],
      count: counts?.[s] ?? null,
    })),
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl">Catalogue migration</h1>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Each customer's repeat work becomes catalogue items, then the
            customer's stock moves to One. Decisions are recorded in the
            customer's migration file with Claude (the
            migrate-customer-catalogue skill) and applied at the next deploy;
            this page shows where each customer has got to.
          </p>
        </div>
        <CatalogueMigrationEnvironmentBadge environment={environment} />
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div
          role="group"
          aria-label="Step"
          className="flex flex-wrap items-center gap-2"
        >
          {chips.map((chip) => (
            <button
              key={chip.value || "all"}
              type="button"
              aria-pressed={step === chip.value}
              onClick={() => setStep(chip.value)}
              className={`px-3 py-1 rounded-full border text-sm transition-colors ${
                step === chip.value
                  ? "bg-primary/10 border-primary text-primary font-medium"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {chip.label}
              {chip.count !== null && (
                <span className="ml-1.5 tabular-nums">
                  {formatCount(chip.count)}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="space-y-1 w-full sm:w-60 sm:ml-auto">
          <Label htmlFor="migration-search">Customer</Label>
          <input
            id="migration-search"
            type="search"
            placeholder="Name, number or code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 border border-border rounded bg-background text-sm w-full"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="migration-sort">Sort by</Label>
          <select
            id="migration-sort"
            className={`${selectClassName} w-auto`}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loadError ? (
        <div
          role="alert"
          className="flex items-center gap-2 text-sm p-4 border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300"
        >
          <AlertTriangle className="size-4 shrink-0" />
          Couldn't load the customers. Please try again.
        </div>
      ) : firstLoad ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="text-muted-foreground">Loading customers…</div>
        </div>
      ) : rows.length === 0 ? (
        <div className="text-muted-foreground text-sm p-8 text-center border border-border rounded-lg">
          {isFiltered
            ? "No customer matches those filters."
            : "No customer holds stock or has repeat work in Tharstern, so there is nothing to migrate."}
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-border bg-surface overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Repeat work</TableHead>
                  <TableHead className="text-right">Held stock</TableHead>
                  <TableHead>Step</TableHead>
                  <TableHead>
                    <span className="sr-only">Open</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((owner) => (
                  <OwnerRow key={owner.customer_id} owner={owner} />
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground">
            {formatCount(total)} customer{total === 1 ? "" : "s"}. Counts for a
            customer with no migration file yet are a quick measure from
            Tharstern's records; its file replaces them.
          </p>
          <ListPagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}

function OwnerRow({ owner }: { owner: CatalogueMigrationOwnerRow }) {
  const reading = readMigrationStep(owner);
  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">
          {owner.customer_name ?? owner.tharstern_customer_code}
        </div>
        <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
          {owner.customer_number && <span>{owner.customer_number}</span>}
          {owner.tharstern_customer_code && (
            <span>{owner.tharstern_customer_code}</span>
          )}
          {owner.is_brandhub && <Badge variant="neutral">BrandHub</Badge>}
        </div>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatCount(owner.repeat_work_count)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatCount(owner.held_stock_count)}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1.5 text-sm">
          {reading.chips.map((chip) => (
            <Badge
              key={chip.label}
              variant={chip.isDone ? "success" : "warning"}
              className="gap-1"
            >
              {chip.isDone && <Check className="size-3" aria-hidden />}
              {chip.label}
            </Badge>
          ))}
          {reading.text && (
            <span
              className={
                owner.step === "cut_over" || owner.step === "ready"
                  ? "font-medium"
                  : "text-muted-foreground"
              }
            >
              {reading.text}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        <Link
          to={paths.settingsCatalogueMigrationCustomer({
            customerId: owner.customer_id,
          })}
          className="text-sm underline"
        >
          Open
        </Link>
      </TableCell>
    </TableRow>
  );
}
